(() => {
  const SELECTOR = '[data-novva-size-options]';
  const PROPERTY_DEFINITIONS = {
    type: 'properties[Ölçü türü]',
    requestedWidth: 'properties[İstenen En (cm)]',
    requestedLength: 'properties[İstenen Boy (cm)]',
    billableWidth: 'properties[Fiyatlandırılan En (cm)]',
    billableLength: 'properties[Fiyatlandırılan Boy (cm)]',
    billableArea: 'properties[Fiyatlandırılan Alan (m²)]',
    unitPrice: 'properties[m² fiyatı]',
    calculatedTotal: 'properties[Hesaplanan toplam]',
  };

  const findProductForm = (component) =>
    component.closest('.product-details')?.querySelector('form[action*="/cart/add"]') || null;

  const ensurePropertyInput = (form, key) => {
    let input = form.querySelector(`[data-novva-size-property="${key}"]`);
    if (input) return input;

    input = document.createElement('input');
    input.type = 'hidden';
    input.name = PROPERTY_DEFINITIONS[key];
    input.dataset.novvaSizeProperty = key;
    form.append(input);
    return input;
  };

  const parseInteger = (value) => {
    if (value === '') return null;
    const number = Number(value);
    return Number.isInteger(number) ? number : null;
  };

  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(cents / 100);

  const formatArea = (area) =>
    `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(area)} m²`;

  const initialize = (component) => {
    if (component.dataset.initialized === 'true') return;
    component.dataset.initialized = 'true';

    const standardToggle = component.querySelector('[data-standard-size-toggle]');
    const customToggle = component.querySelector('[data-custom-size-toggle]');
    const buttons = [standardToggle, customToggle].filter(Boolean);
    const customFields = component.querySelector('[data-custom-size-fields]');
    const standardHint = component.querySelector('[data-standard-size-hint]');
    const customWidth = component.querySelector('[data-custom-width]');
    const customLength = component.querySelector('[data-custom-length]');
    const error = component.querySelector('[data-size-error]');
    const quotePanel = component.querySelector('[data-size-quote]');
    const pricingStatus = component.querySelector('[data-pricing-status]');
    const propertyType = component.querySelector('[data-size-property="type"]');
    const propertyWidth = component.querySelector('[data-size-property="width"]');
    const propertyLength = component.querySelector('[data-size-property="length"]');
    const productDetails = component.closest('.product-details');

    const rollWidthsCm = (component.dataset.rollWidths || '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isInteger(value) && value > 0);
    const maxLengthCm = Number(component.dataset.maxLengthCm) || 3000;
    const currency = component.dataset.currency || 'TRY';

    let customTouched = false;

    const getSelection = () => ({
      mode: propertyType.value === 'Özel ölçü' ? 'custom' : 'standard',
      widthCm: parseInteger(propertyWidth.value),
      lengthCm: parseInteger(propertyLength.value),
      pricingReady: component.dataset.pricingReady === 'true',
    });

    const getQuote = (selection) =>
      window.NovvaPriceEngine?.quoteSize({
        mode: selection.mode,
        widthCm: selection.widthCm,
        lengthCm: selection.lengthCm,
        unitPriceCents: Number(component.dataset.unitPriceCents) || null,
        currency,
        roundingPolicy: {
          type: 'roll_width_step_up',
          rollWidthsCm,
          maxLengthCm,
        },
      }) ?? { status: 'pending', reason: 'engine_unavailable' };

    const setSelectedMode = (activeButton) => {
      buttons.forEach((button) => {
        const selected = button === activeButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
    };

    const toggleStandardControls = (isCustom) => {
      document.body.classList.toggle('novva-custom-size-selected', isCustom);

      const variantPicker = productDetails?.querySelector('variant-picker');
      if (variantPicker) variantPicker.hidden = isCustom;
      if (standardHint) standardHint.hidden = isCustom;
      if (customFields) customFields.hidden = !isCustom;

      const acceleratedCheckout = productDetails?.querySelector('.accelerated-checkout-block');
      if (acceleratedCheckout) {
        if (isCustom) {
          if (!Object.hasOwn(acceleratedCheckout.dataset, 'novvaWasHidden')) {
            acceleratedCheckout.dataset.novvaWasHidden = String(acceleratedCheckout.hidden);
          }
          acceleratedCheckout.hidden = true;
        } else if (acceleratedCheckout.dataset.novvaWasHidden === 'false') {
          acceleratedCheckout.hidden = false;
          delete acceleratedCheckout.dataset.novvaWasHidden;
        } else if (Object.hasOwn(acceleratedCheckout.dataset, 'novvaWasHidden')) {
          delete acceleratedCheckout.dataset.novvaWasHidden;
        }
      }

      const stickyAddToCart = document.querySelector(
        `sticky-add-to-cart[data-product-id="${component.dataset.productId}"]`
      );
      const stickyButton = stickyAddToCart?.querySelector('.sticky-add-to-cart__button');
      if (stickyAddToCart) {
        if (isCustom) {
          if (!Object.hasOwn(stickyAddToCart.dataset, 'novvaVariantAvailable')) {
            stickyAddToCart.dataset.novvaVariantAvailable = stickyAddToCart.dataset.variantAvailable;
          }
          stickyAddToCart.dataset.variantAvailable = 'false';
          if (stickyButton && !stickyButton.disabled) stickyButton.dataset.novvaCustomDisabled = 'true';
          if (stickyButton) stickyButton.disabled = true;
        } else if (Object.hasOwn(stickyAddToCart.dataset, 'novvaVariantAvailable')) {
          stickyAddToCart.dataset.variantAvailable = stickyAddToCart.dataset.novvaVariantAvailable;
          delete stickyAddToCart.dataset.novvaVariantAvailable;
          if (stickyButton?.dataset.novvaCustomDisabled === 'true') {
            stickyButton.disabled = false;
            delete stickyButton.dataset.novvaCustomDisabled;
          }
        }
      }
    };

    const setPurchaseState = (form, selection, quote) => {
      const addButton = form?.querySelector('button[type="submit"][name="add"], add-to-cart-component button');
      if (!addButton) return;

      /*
       * Custom-size checkout price is created by the PC Price Calculator app,
       * not by the native Shopify product form. Keep the theme's own add button
       * disabled for every custom-size state so an app-scope/configuration miss
       * cannot add the base variant at a stale fixed price.
       */
      if (selection.mode === 'custom') {
        if (!addButton.disabled) addButton.dataset.novvaCustomDisabled = 'true';
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
      } else if (addButton.dataset.novvaCustomDisabled === 'true') {
        addButton.disabled = false;
        addButton.removeAttribute('aria-disabled');
        delete addButton.dataset.novvaCustomDisabled;
      }

      if (pricingStatus) {
        pricingStatus.hidden = !(
          selection.mode === 'custom' &&
          ((quote.status === 'ready' && !selection.pricingReady) || quote.status === 'pending')
        );
      }
    };

    const errorMessage = (quote) => {
      if (quote.status !== 'invalid') return '';
      if (quote.errors.includes('width_above_max')) return `Maksimum en ${quote.maxWidthCm} cm olabilir.`;
      if (quote.errors.includes('length_above_max')) return `Maksimum boy ${quote.maxLengthCm} cm olabilir.`;
      if (quote.errors.includes('invalid_width') && quote.errors.includes('invalid_length')) {
        return 'En ve boy değerlerini pozitif tam sayı olarak girin.';
      }
      if (quote.errors.includes('invalid_width')) return 'En değerini pozitif tam sayı olarak girin.';
      if (quote.errors.includes('invalid_length')) return 'Boy değerini pozitif tam sayı olarak girin.';
      return 'Ölçüleri kontrol edip tekrar deneyin.';
    };

    const renderQuote = (selection, quote, { announce = false } = {}) => {
      const showError = selection.mode === 'custom' && quote.status === 'invalid' && (customTouched || announce);
      if (error) {
        error.textContent = showError ? errorMessage(quote) : '';
        error.hidden = !showError;
      }

      [customWidth, customLength].filter(Boolean).forEach((input) => {
        input.setAttribute('aria-invalid', String(showError));
      });

      if (!quotePanel) return;
      quotePanel.hidden = quote.status !== 'ready';
      if (quote.status !== 'ready') return;

      quotePanel.querySelector('[data-quote-requested]').textContent =
        `${quote.requestedWidthCm} × ${quote.requestedLengthCm} cm`;
      quotePanel.querySelector('[data-quote-billable]').textContent =
        `${quote.billableWidthCm} × ${quote.billableLengthCm} cm`;
      quotePanel.querySelector('[data-quote-area]').textContent = formatArea(quote.billableAreaM2);
      quotePanel.querySelector('[data-quote-unit-price]').textContent = formatMoney(quote.unitPriceCents, currency);
      quotePanel.querySelector('[data-quote-total]').textContent = formatMoney(quote.totalPriceCents, currency);
    };

    const syncProperties = (form, selection, quote) => {
      if (!form) return;

      const isCustomReady = selection.mode === 'custom' && quote.status === 'ready';
      const values = isCustomReady
        ? {
            type: 'Özel ölçü',
            requestedWidth: quote.requestedWidthCm,
            requestedLength: quote.requestedLengthCm,
            billableWidth: quote.billableWidthCm,
            billableLength: quote.billableLengthCm,
            billableArea: formatArea(quote.billableAreaM2),
            unitPrice: formatMoney(quote.unitPriceCents, currency),
            calculatedTotal: formatMoney(quote.totalPriceCents, currency),
          }
        : {};

      Object.keys(PROPERTY_DEFINITIONS).forEach((key) => {
        const input = ensurePropertyInput(form, key);
        input.disabled = !isCustomReady;
        input.value = isCustomReady ? values[key] : '';
      });
    };

    const syncProductForm = ({ announce = false } = {}) => {
      const selection = getSelection();
      const quote = getQuote(selection);
      const form = findProductForm(component);

      component.dataset.valid = String(selection.mode === 'standard' || quote.status === 'ready');
      renderQuote(selection, quote, { announce });
      syncProperties(form, selection, quote);
      setPurchaseState(form, selection, quote);

      component.dispatchEvent(
        new CustomEvent('novva:size-change', {
          bubbles: true,
          detail: { ...selection, valid: component.dataset.valid === 'true', quote },
        })
      );
      component.dispatchEvent(
        new CustomEvent('novva:price-quote', {
          bubbles: true,
          detail: quote,
        })
      );
    };

    standardToggle?.addEventListener('click', () => {
      setSelectedMode(standardToggle);
      propertyType.value = 'Standart ölçü';
      propertyWidth.value = '';
      propertyLength.value = '';
      customTouched = false;
      toggleStandardControls(false);
      syncProductForm();
    });

    customToggle?.addEventListener('click', () => {
      setSelectedMode(customToggle);
      propertyType.value = 'Özel ölçü';
      propertyWidth.value = customWidth.value;
      propertyLength.value = customLength.value;
      toggleStandardControls(true);
      syncProductForm();
      requestAnimationFrame(() => customWidth.focus());
    });

    const syncCustomValues = () => {
      customTouched = true;
      propertyWidth.value = customWidth.value;
      propertyLength.value = customLength.value;
      syncProductForm({ announce: true });
    };

    customWidth?.addEventListener('input', syncCustomValues);
    customLength?.addEventListener('input', syncCustomValues);
    customWidth?.addEventListener('blur', () => syncProductForm({ announce: true }));
    customLength?.addEventListener('blur', () => syncProductForm({ announce: true }));

    component.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const currentIndex = buttons.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      const next = buttons[(currentIndex + direction + buttons.length) % buttons.length];
      next.focus();
      next.click();
    });

    if (productDetails) {
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          toggleStandardControls(getSelection().mode === 'custom');
          syncProductForm();
        });
      }).observe(productDetails, { childList: true, subtree: true });
    }

    toggleStandardControls(false);
    syncProductForm();
  };

  const initializeAll = (root = document) => root.querySelectorAll(SELECTOR).forEach(initialize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
